// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Zod schema for the releases VCA collection.
 *
 * Git releases / tags from any connected provider.
 *
 * Identity tuple: (gitConnectionId, repoId, tagName)
 * ID prefix: rel_
 */

import { z } from 'zod'

export const releasesSchema = z.object({
  id: z.string().max(100),
  orgId: z.string().max(100),
  workspaceId: z.string().max(100).optional(),

  // ── Git connection reference ──────────────────────────────────────────
  gitConnectionId: z.string().max(100),
  codebaseId: z.string().max(100).optional(),

  // ── Identity tuple ────────────────────────────────────────────────────
  repoId: z.string().max(200),
  /** Tag name used to create the release (e.g. 'v1.2.3'). */
  tagName: z.string().max(500),

  // ── Release metadata ──────────────────────────────────────────────────
  /** Human-readable release title (may differ from tagName). */
  name: z.string().max(500).optional(),
  /** Release body / changelog text. */
  body: z.string().optional(),
  /** True if marked as a pre-release by the author. */
  isPrerelease: z.boolean().default(false),
  /** True if the release is still a draft (not yet published). */
  isDraft: z.boolean().default(false),
  /** When the release was published. Null for draft releases. */
  publishedAt: z.string().datetime().nullable().optional(),
  /** Login of the user who created the release. */
  authorLogin: z.string().max(200),

  // ── Provider-specific extras ──────────────────────────────────────────
  providerData: z.record(z.string(), z.unknown()).optional(),
})

export type ReleasesInput = z.infer<typeof releasesSchema>
